import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, PlusCircle, Smartphone, MapPin, Building2, Trash2, Eye, Loader2, ChevronLeft, ChevronRight, Search, X, History } from "lucide-react";
import { useAuth } from '../contexts/AuthContext';
import { UserRole, Client, Sede, ClienteAttivita } from '../types';
import { ClientService, SedeService } from '../services/apiService';
import { useToast } from "../components/ToastContext";
import { FiltroMulti } from '../components/FiltroMulti';
import { getClientDisplayName, getClientEmail, getClientTelefono, getClientReferente } from '../utils/clientDisplay';
import {
    FiltriClienti, FILTRI_CLIENTI_VUOTI, TIPI_MANDATO, PERIODI, ORDINI, TipoMandato,
    FiltroTipoCliente, FiltroAmbito, FiltroPeriodo, FiltroStato, OrdineClienti,
    contaFiltriAttivi, filtraClienti,
} from '../utils/filtriClienti';

// I filtri restano impostati per la sessione, anche tornando dalla scheda di un cliente.
const CHIAVE_FILTRI = 'ud-filtri-clienti';
const leggiFiltri = (): FiltriClienti => {
    try {
        const raw = sessionStorage.getItem(CHIAVE_FILTRI);
        const f: FiltriClienti = raw ? { ...FILTRI_CLIENTI_VUOTI, ...JSON.parse(raw) } : FILTRI_CLIENTI_VUOTI;
        return ORDINI.some(([v]) => v === f.ordine) ? f : { ...f, ordine: 'NOME' };
    } catch {
        return FILTRI_CLIENTI_VUOTI;
    }
};

// Pulsanti a segmenti come il filtro tipologia del calendario.
function Segmenti<V extends string>({ valore, opzioni, onChange }: {
    valore: V; opzioni: [V, string, string?][]; onChange: (v: V) => void;
}) {
    return (
        <div className="flex bg-gray-200/80 p-1 rounded-xl shadow-inner border border-gray-300 w-full sm:w-auto">
            {opzioni.map(([v, etichetta, colore]) => (
                <button
                    key={v}
                    type="button"
                    onClick={() => onChange(v)}
                    className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg font-semibold text-xs transition-all whitespace-nowrap ${valore === v ? `bg-white shadow-sm ${colore || 'text-slate-800'}` : 'text-gray-600 hover:bg-gray-300/50'}`}
                >
                    {etichetta}
                </button>
            ))}
        </div>
    );
}

const selectCls = 'w-full sm:w-auto px-3 py-2 rounded-xl border border-gray-300 bg-white text-xs font-semibold text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200';
const selectAttivoCls = 'w-full sm:w-auto px-3 py-2 rounded-xl border border-blue-300 bg-blue-50 text-xs font-semibold text-blue-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200';

export const ClientiView: React.FC = () => {
    const navigate = useNavigate();
    const { user, currentSedeId } = useAuth();
    const { addToast } = useToast();

    // Stati per i dati reali
    const [clients, setClients] = useState<Client[]>([]);
    const [sedi, setSedi] = useState<Sede[]>([]);
    const [attivita, setAttivita] = useState<Record<string, ClienteAttivita>>({});
    const [loading, setLoading] = useState(true);
    const [filtri, setFiltri] = useState<FiltriClienti>(leggiFiltri);
    const aggiorna = (p: Partial<FiltriClienti>) => setFiltri(f => ({ ...f, ...p }));
    useEffect(() => {
        try { sessionStorage.setItem(CHIAVE_FILTRI, JSON.stringify(filtri)); } catch { /* storage non disponibile */ }
    }, [filtri]);

    // Caricamento Dati
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [clientsRes, sediRes, riepilogo] = await Promise.all([
                    ClientService.getAll(),
                    SedeService.getAll(),
                    ClientService.riepilogoAttivita().catch(() => ({} as Record<string, ClienteAttivita>)),
                ]);

                // La risposta può essere un array o un wrapper
                const clientsList = Array.isArray(clientsRes) ? clientsRes : (clientsRes as any).data || [];
                setClients(clientsList);

                const sediList = Array.isArray(sediRes) ? sediRes : (sediRes as any).data || [];
                setSedi(sediList);

                // Mandati, servizi e mesi di lavoro per cliente: la lista mandati è paginata e non basta.
                setAttivita(riepilogo || {});

            } catch (error) {
                console.error("Errore caricamento dati:", error);
                addToast("Impossibile caricare la lista clienti", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [addToast, currentSedeId]);

    // Filtro Clienti e Arricchimento Dati
    const clientiFiltrati = useMemo(() => {
        if (!user) return { tutti: 0, risultato: [] };

        // Filtro per ruolo/sede
        const visibleClients = clients.filter(cliente => {
            // ADMIN con "Tutte le sedi": mostra tutto
            if (user.role === UserRole.ADMIN && currentSedeId === 'ALL') {
                return true;
            }

            // Id sede letto in modo tollerante
            const clientSedeId = cliente.sedeId || (cliente as any).sede?.id;

            if (user.role === UserRole.ADMIN) {
                // Senza sede l'admin vede comunque il cliente, così può correggerlo.
                if (!clientSedeId) return true;

                return String(clientSedeId) === String(currentSedeId);
            }

            if (user.role === UserRole.RESPONSABILE) {
                // Senza sede il responsabile non lo vede
                if (!clientSedeId) return false;
                return String(clientSedeId) === String(currentSedeId ?? user.sedeId);
            }

            return true;
        });

        // Tutti i mandati non eliminati del cliente, qualsiasi stato.
        const conMandati = visibleClients.map(c => {
            const a = attivita[String(c.id)];
            const mandati = a ? a.mandatiInvestigativi + a.mandatiBonifica + a.mandatiConsulenza : 0;
            return { ...c, praticheAttive: mandati };
        });
        return { tutti: conMandati.length, risultato: filtraClienti(conMandati, attivita, filtri) };

    }, [user, currentSedeId, clients, attivita, filtri]);
    const filteredClienti = clientiFiltrati.risultato;

    // 20 clienti per pagina. Si torna a pagina 1 quando cambia l'insieme visibile
    // e la pagina resta sempre nel range.
    const PAGE_SIZE = 20;
    const [page, setPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(filteredClienti.length / PAGE_SIZE));
    useEffect(() => { setPage(1); }, [currentSedeId, filtri]);
    const filtriAttivi = contaFiltriAttivi(filtri);
    useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
    const pageClienti = filteredClienti.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Helper per ottenere nome sede dai dati reali
    const getSedeName = (id?: number) => {
        if (!id) return 'N/D';
        const sede = sedi.find(s => String(s.id) === String(id));
        return sede ? sede.name : 'Sede eliminata';
    };

    // Eliminazione
    const handleDelete = async (e: React.MouseEvent, cliente: Client) => {
        e.stopPropagation(); // Evita redirect al dettaglio

        const display = getClientDisplayName(cliente);
        const confirmMessage = (cliente.praticheAttive || 0) > 0
            ? `ATTENZIONE: ${display} ha ${cliente.praticheAttive} mandati associati.\n\nEliminandolo, non sarà più visibile in questa lista, ma i dati storici dei mandati rimarranno salvati.\n\nVuoi procedere comunque?`
            : `Sei sicuro di voler eliminare ${display}?`;

        if (window.confirm(confirmMessage)) {
            try {
                if (cliente.id) {
                    await ClientService.delete(Number(cliente.id));
                    setClients(prev => prev.filter(c => c.id !== cliente.id));
                    addToast('Cliente rimosso dall\'anagrafica', 'success');
                }
            } catch (error: any) {
                console.error(error);
                // Il BE risponde 409 con un messaggio leggibile (es. mandati non conclusi):
                // lo mostriamo al posto di quello generico.
                const msg = error?.response?.data?.message || "Errore durante l'eliminazione";
                addToast(msg, "error");
            }
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Gestione Clienti</h1>
                    <p className="text-sm text-gray-500">Visualizza e gestisci l'anagrafica clienti.</p>
                </div>
                <button
                    onClick={() => navigate('/clienti/nuovo')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm"
                >
                    <PlusCircle size={18}/> Aggiungi Cliente
                </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-3 lg:p-4 space-y-3 shadow-sm">
                <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                        <input
                            value={filtri.cerca}
                            onChange={e => aggiorna({ cerca: e.target.value })}
                            placeholder="Cerca per nome, ragione sociale, email, telefono, codice fiscale o P. IVA"
                            className="w-full pl-9 pr-9 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
                        />
                        {filtri.cerca && (
                            <button type="button" onClick={() => aggiorna({ cerca: '' })} title="Svuota ricerca"
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700">
                                <X size={14}/>
                            </button>
                        )}
                    </div>
                    <select
                        value={filtri.ordine}
                        onChange={e => aggiorna({ ordine: e.target.value as OrdineClienti })}
                        className={selectCls}
                        title="Ordinamento"
                    >
                        {ORDINI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </div>

                <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-2">
                    <Segmenti<FiltroTipoCliente>
                        valore={filtri.tipo}
                        onChange={v => aggiorna({ tipo: v })}
                        opzioni={[['TUTTI', 'Tutti'], ['PRIVATI', 'Privati', 'text-blue-700'], ['AZIENDE', 'Aziende', 'text-purple-700']]}
                    />
                    <Segmenti<FiltroAmbito>
                        valore={filtri.ambito}
                        onChange={v => aggiorna({ ambito: v })}
                        opzioni={[['TUTTI', 'Tutti i servizi'], ['INVESTIGATIVI', 'Servizi Investigativi', 'text-indigo-700'], ['FIDUCIARI', 'Servizi Fiduciari', 'text-emerald-700']]}
                    />
                    <FiltroMulti
                        placeholder="Tutti i tipi di mandato"
                        opzioni={TIPI_MANDATO}
                        selezionati={filtri.tipiMandato}
                        onChange={v => aggiorna({ tipiMandato: v as TipoMandato[] })}
                    />
                    <select
                        value={filtri.periodo}
                        onChange={e => aggiorna({ periodo: e.target.value as FiltroPeriodo })}
                        className={filtri.periodo !== 'TUTTO' ? selectAttivoCls : selectCls}
                        title="Periodo di lavoro"
                    >
                        {PERIODI.map(([v, l]) => <option key={v} value={v}>{v === 'TUTTO' ? 'Periodo di lavoro: qualsiasi' : l}</option>)}
                    </select>
                    {filtri.periodo === 'PERSONALIZZATO' && (
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span>dal</span>
                            <input type="month" value={filtri.dal} onChange={e => aggiorna({ dal: e.target.value })} className={selectCls}/>
                            <span>al</span>
                            <input type="month" value={filtri.al} onChange={e => aggiorna({ al: e.target.value })} className={selectCls}/>
                        </div>
                    )}
                    <select
                        value={filtri.stato}
                        onChange={e => aggiorna({ stato: e.target.value as FiltroStato })}
                        className={filtri.stato !== 'TUTTI' ? selectAttivoCls : selectCls}
                        title="Stato dei lavori"
                    >
                        <option value="TUTTI">Qualsiasi stato</option>
                        <option value="APERTI">Con lavori in corso</option>
                        <option value="CONCLUSI">Solo lavori conclusi</option>
                        <option value="SENZA">Senza mandati né servizi</option>
                    </select>
                    {(filtriAttivi > 0 || filtri.cerca) && (
                        <button
                            type="button"
                            onClick={() => setFiltri(f => ({ ...FILTRI_CLIENTI_VUOTI, ordine: f.ordine }))}
                            className="text-xs font-bold text-blue-600 hover:text-blue-800 px-2 py-1 self-start lg:self-auto"
                        >
                            Azzera filtri
                        </button>
                    )}
                </div>

                <p className="text-xs text-gray-500">
                    {filtriAttivi > 0 || filtri.cerca
                        ? <><b className="text-gray-900">{filteredClienti.length}</b> clienti trovati su {clientiFiltrati.tutti}</>
                        : <><b className="text-gray-900">{filteredClienti.length}</b> clienti</>}
                    {filtri.periodo !== 'TUTTO' && ' · nel periodo: mandati aperti, incarichi o turni svolti'}
                </p>
            </div>

            {filteredClienti.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    {pageClienti.map(c => (
                        <div
                            key={c.id}
                            onClick={() => navigate(`/clienti/${c.id}`)}
                            className="bg-white p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 flex flex-col hover:shadow-md transition-shadow group cursor-pointer relative"
                        >
                            {(() => {
                                const isAzienda = c.tipo === 'Azienda';
                                const ragioneSociale = getClientDisplayName(c);
                                const referente = getClientReferente(c);
                                // Azienda: "Ragione Sociale (Referente)", con fallback all'uno o all'altro se manca.
                                const titolo = isAzienda
                                    ? (ragioneSociale
                                        ? `${ragioneSociale}${referente ? ` (${referente})` : ''}`
                                        : referente)
                                    : ragioneSociale;
                                const email = getClientEmail(c);
                                const telefono = getClientTelefono(c);
                                return (
                                    <>
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${isAzienda ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                                                {isAzienda ? <Building2 size={20}/> : (c.nome || '?').charAt(0).toUpperCase()}
                                            </div>
                                            <div className="overflow-hidden">
                                                <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate" title={titolo}>
                                                    {titolo}
                                                </h3>
                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">{c.tipo}</span>
                                            </div>
                                        </div>

                                        <div className="space-y-2 text-sm text-gray-600 flex-1">
                                            <div className="flex items-center gap-2 truncate" title={email}>
                                                <Mail size={14} className="text-gray-400 shrink-0"/> {email || '—'}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Smartphone size={14} className="text-gray-400 shrink-0"/> {telefono || '—'}
                                            </div>
                                            {attivita[String(c.id)]?.ultimaAttivita && (
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                    <History size={14} className="text-gray-400 shrink-0"/>
                                                    Ultima attività {new Date(`${attivita[String(c.id)].ultimaAttivita}T00:00:00`).toLocaleDateString('it-IT')}
                                                </div>
                                            )}
                                            {user?.role === UserRole.ADMIN && currentSedeId === 'ALL' && (
                                                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-dashed">
                                                    <MapPin size={14} className="text-orange-500 shrink-0"/>
                                                    <span className="text-xs font-medium text-orange-600 truncate">
                                                        {getSedeName(c.sedeId)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}

                            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                                {(c.praticheAttive || 0) > 0 ? (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/clienti/${c.id}#mandati`); }}
                                        className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                                        title="Vedi mandati associati"
                                    >
                                        <strong className="text-gray-900">{c.praticheAttive}</strong> Mandati Associati
                                    </button>
                                ) : (
                                    <span className="text-xs font-medium text-gray-400">
                                        <strong className="text-gray-900">0</strong> Mandati Associati
                                    </span>
                                )}
                                {(attivita[String(c.id)]?.servizi || 0) > 0 && (
                                    <span className="text-xs font-medium text-emerald-700 ml-2 mr-auto">
                                        <strong>{attivita[String(c.id)].servizi}</strong> {attivita[String(c.id)].servizi === 1 ? 'Servizio' : 'Servizi'}
                                    </span>
                                )}

                                <div className="flex gap-2">
                                    {/* Solo il titolare può eliminare clienti (ClientController.delete è ADMIN-only). */}
                                    {user?.role === UserRole.ADMIN && (
                                        <button
                                            onClick={(e) => handleDelete(e, c)}
                                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                            title="Elimina dall'anagrafica"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                    <button className="text-blue-600 text-sm font-medium hover:underline flex items-center gap-1">
                                        Vedi Scheda <Eye size={14}/>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}

            {filteredClienti.length > PAGE_SIZE && (
                <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3">
                    <span className="text-sm text-gray-500">
                        Clienti {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredClienti.length)} di <b className="text-gray-900">{filteredClienti.length}</b>
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            disabled={page <= 1}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft size={16}/> Precedente
                        </button>
                        <span className="text-sm font-semibold text-gray-700 px-1">Pagina {page} di {totalPages}</span>
                        <button
                            onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            disabled={page >= totalPages}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Successiva <ChevronRight size={16}/>
                        </button>
                    </div>
                </div>
            )}

            {filteredClienti.length === 0 && (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200 text-gray-500">
                    <p>Nessun cliente trovato.</p>
                    <p className="text-xs mt-2">Prova a cambiare i filtri o aggiungi un nuovo cliente.</p>
                </div>
            )}
        </div>
    );
};

export default ClientiView;